import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase/supabaseClient';

const { 
  FiHome, FiUsers, FiSettings, FiActivity, FiDollarSign, 
  FiShoppingBag, FiMenu, FiBell, FiSearch, FiMoreVertical,
  FiArrowUp, FiArrowDown, FiPlusCircle, FiRefreshCw, FiDatabase
} = FiIcons;

function AdminDashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSimulating, setIsSimulating] = useState(false);

  // --- Realtime Data Fetching ---
  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    fetchOrders();

    // Subscribe to Realtime changes
    const channel = supabase
      .channel('orders_channel')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, (payload) => {
        // Optimistic update or re-fetch. Re-fetching is safer for consistency in this demo.
        fetchOrders(); 
      })
      .subscribe();

    return () => {
      if (supabase) supabase.removeChannel(channel);
    };
  }, []);

  async function fetchOrders() {
    if (!supabase) return;
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50); // Get last 50 for stats/charts

      if (error) throw error;
      if (data) setOrders(data);
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  }

  // --- Derived Statistics (Single Source of Truth) ---
  const stats = useMemo(() => {
    if (!orders.length) return { revenue: 0, count: 0, pending: 0, avg: 0 };

    const totalRevenue = orders.reduce((acc, order) => acc + Number(order.amount), 0);
    const completedOrders = orders.filter(o => o.status === 'Completed').length;
    const pendingOrders = orders.filter(o => o.status === 'Pending').length;

    return {
      revenue: totalRevenue.toFixed(2),
      count: orders.length,
      pending: pendingOrders,
      avg: (totalRevenue / orders.length).toFixed(2)
    };
  }, [orders]);

  // --- Chart Data Preparation ---
  const chartOption = useMemo(() => {
    // Group orders by date (simple version)
    const dataMap = {};
    // Reverse orders to process oldest first for the chart
    [...orders].reverse().forEach(order => {
      const date = new Date(order.created_at).toLocaleDateString();
      if (!dataMap[date]) dataMap[date] = 0;
      dataMap[date] += Number(order.amount);
    });

    const dates = Object.keys(dataMap);
    const values = Object.values(dataMap);

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#1f2937',
        textStyle: { color: '#fff' },
        borderWidth: 0,
        formatter: (params) => `$${params[0].value}`
      },
      grid: {
        left: '3%', right: '4%', bottom: '3%', top: '10%',
        containLabel: true, borderColor: '#374151'
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: dates.length > 0 ? dates : ['No Data'],
        axisLine: { lineStyle: { color: '#4b5563' } },
        axisLabel: { color: '#9ca3af' }
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: '#374151' } },
        axisLabel: { color: '#9ca3af', formatter: '${value}' }
      },
      series: [{
        name: 'Revenue',
        type: 'line',
        smooth: true,
        lineStyle: { width: 3, color: '#f97316' },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(249, 115, 22, 0.5)' },
              { offset: 1, color: 'rgba(249, 115, 22, 0)' }
            ]
          }
        },
        data: values.length > 0 ? values : [0]
      }]
    };
  }, [orders]);

  // --- Simulate Live Data ---
  const simulateLiveTransaction = async () => {
    if (!supabase) return;
    setIsSimulating(true);
    const names = ['Alice Corp', 'Bob Ltd', 'Charlie Inc', 'Delta Force', 'Echo Base'];
    const statuses = ['Completed', 'Completed', 'Completed', 'Pending']; // Weighted towards completed
    
    const randomOrder = {
      customer_name: names[Math.floor(Math.random() * names.length)],
      amount: (Math.random() * 500 + 50).toFixed(2),
      status: statuses[Math.floor(Math.random() * statuses.length)],
    };

    await supabase.from('orders').insert([randomOrder]);
    
    // Simulate a small delay for button feedback
    setTimeout(() => setIsSimulating(false), 500);
  };

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // --- Render Error State if Supabase is Missing ---
  if (!supabase) {
    return (
      <div className="flex h-screen items-center justify-center bg-black text-white p-4">
        <div className="text-center p-8 max-w-md border border-white/10 rounded-2xl bg-white/5 backdrop-blur-sm shadow-2xl">
           <div className="bg-orange-500/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
             <SafeIcon icon={FiIcons.FiDatabase} className="text-4xl text-orange-500" />
           </div>
           <h2 className="text-2xl font-bold mb-3">Database Not Connected</h2>
           <p className="text-gray-400 mb-8 leading-relaxed">
             This dashboard requires a Supabase connection to display realtime data. 
             Please connect your project to Supabase to enable this feature.
           </p>
           <div className="p-4 bg-black/30 rounded-lg text-sm font-mono text-gray-500 break-all border border-white/5">
             Waiting for environment variables...
           </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-black text-white overflow-hidden font-sans selection:bg-orange-500 selection:text-white">
      
      {/* --- Sidebar --- */}
      <motion.div 
        initial={false}
        animate={{ width: sidebarOpen ? '260px' : '80px' }}
        className="hidden md:flex flex-col border-r border-white/10 bg-black/50 backdrop-blur-md z-20 h-full transition-all duration-300"
      >
        <div className="h-20 flex items-center justify-center border-b border-white/10">
          {sidebarOpen ? (
            <h1 className="text-2xl font-black tracking-tighter text-white">
              MONKEY<span className="text-orange-500">DATA</span>
            </h1>
          ) : (
            <h1 className="text-2xl font-black text-orange-500">M</h1>
          )}
        </div>
        <nav className="flex-1 py-6 px-3 space-y-2">
          <SidebarItem icon={FiHome} label="Live Dashboard" active isOpen={sidebarOpen} />
          <SidebarItem icon={FiUsers} label="Customers" isOpen={sidebarOpen} />
          <SidebarItem icon={FiShoppingBag} label="Orders" isOpen={sidebarOpen} />
          <SidebarItem icon={FiActivity} label="Performance" isOpen={sidebarOpen} />
        </nav>
      </motion.div>

      {/* --- Main Content --- */}
      <div className="flex-1 flex flex-col h-full overflow-hidden relative">
        <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-gray-900 to-black pointer-events-none -z-10" />
        
        {/* Header */}
        <header className="h-20 border-b border-white/10 flex items-center justify-between px-6 lg:px-10 bg-black/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="flex items-center gap-4">
            <button onClick={toggleSidebar} className="hidden md:block p-2 rounded-lg hover:bg-white/10">
               <SafeIcon icon={FiMenu} className="text-xl" />
            </button>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              Realtime Overview
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <button 
              onClick={simulateLiveTransaction}
              disabled={isSimulating}
              className="hidden sm:flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSimulating ? (
                 <SafeIcon icon={FiRefreshCw} className="animate-spin" />
              ) : (
                 <SafeIcon icon={FiPlusCircle} />
              )}
              Simulate Live Order
            </button>
          </div>
        </header>

        {/* Scrollable Content */}
        <main className="flex-1 overflow-y-auto p-6 lg:p-10 space-y-8">
          
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard 
              title="Total Revenue" 
              value={`$${stats.revenue}`} 
              subtext="Lifetime Volume"
              icon={FiDollarSign} 
              color="text-green-500"
            />
            <StatCard 
              title="Total Orders" 
              value={stats.count} 
              subtext="All Time"
              icon={FiShoppingBag} 
              color="text-blue-500"
            />
            <StatCard 
              title="Avg. Order Value" 
              value={`$${stats.avg}`}
              subtext="Per Transaction" 
              icon={FiActivity} 
              color="text-purple-500"
            />
             <StatCard 
              title="Pending Orders" 
              value={stats.pending}
              subtext="Needs Attention" 
              icon={FiBell} 
              color="text-orange-500"
            />
          </div>

          {/* Charts & Content Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Chart */}
            <div className="lg:col-span-2 bg-white/5 border border-white/10 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold">Revenue Trend</h3>
                <span className="text-xs text-gray-400 bg-black/30 px-2 py-1 rounded">Live Data</span>
              </div>
              <div className="h-80 w-full">
                {loading ? (
                  <div className="h-full flex items-center justify-center text-gray-500">Loading Chart...</div>
                ) : (
                  <ReactECharts option={chartOption} style={{ height: '100%', width: '100%' }} />
                )}
              </div>
            </div>

            {/* Quick Actions / Info */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col justify-between">
              <div>
                <h3 className="text-lg font-bold mb-4">Live Control</h3>
                <p className="text-sm text-gray-400 mb-6">
                  Click the button below to inject a real record into the Supabase 'orders' table. 
                  The dashboard will update instantly via Websockets.
                </p>
                <div className="space-y-3">
                   <div className="flex justify-between text-sm p-3 bg-white/5 rounded-lg">
                      <span className="text-gray-400">Database Status</span>
                      <span className="text-green-400 font-bold">Connected</span>
                   </div>
                   <div className="flex justify-between text-sm p-3 bg-white/5 rounded-lg">
                      <span className="text-gray-400">Realtime Channel</span>
                      <span className="text-green-400 font-bold">Active</span>
                   </div>
                </div>
              </div>
              <button 
                onClick={simulateLiveTransaction}
                className="w-full mt-6 py-3 bg-white/10 hover:bg-white/20 border border-white/10 rounded-xl font-bold transition-all flex items-center justify-center gap-2"
              >
                 <SafeIcon icon={FiPlusCircle} />
                 Generate Transaction
              </button>
            </div>
          </div>

          {/* Recent Orders Table */}
          <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-bold">Realtime Transactions</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-white/5 text-gray-400 text-sm uppercase tracking-wider">
                    <th className="p-4 font-medium">Order ID</th>
                    <th className="p-4 font-medium">Customer</th>
                    <th className="p-4 font-medium">Date</th>
                    <th className="p-4 font-medium">Amount</th>
                    <th className="p-4 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="text-sm divide-y divide-white/5">
                  {loading ? (
                    <tr><td colSpan="5" className="p-8 text-center text-gray-500">Connecting to Supabase...</td></tr>
                  ) : orders.length === 0 ? (
                    <tr><td colSpan="5" className="p-8 text-center text-gray-500">No orders yet. Click "Simulate" to create one.</td></tr>
                  ) : (
                    orders.slice(0, 10).map((order) => (
                      <motion.tr 
                        key={order.id} 
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="hover:bg-white/5 transition-colors"
                      >
                        <td className="p-4 font-mono text-white/50 text-xs">{order.id.slice(0, 8)}...</td>
                        <td className="p-4 font-medium">{order.customer_name}</td>
                        <td className="p-4 text-gray-500">{new Date(order.created_at).toLocaleTimeString()}</td>
                        <td className="p-4 font-bold text-orange-400">${order.amount}</td>
                        <td className="p-4">
                          <StatusBadge status={order.status} />
                        </td>
                      </motion.tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </main>
      </div>
    </div>
  );
}

// --- Sub-components ---

const SidebarItem = ({ icon, label, active, isOpen }) => (
  <button 
    className={`w-full flex items-center gap-4 p-3 rounded-xl transition-all duration-200 group
      ${active ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20' : 'text-gray-400 hover:bg-white/5 hover:text-white'}
      ${!isOpen && 'justify-center'}
    `}
  >
    <SafeIcon icon={icon} className="text-xl shrink-0" />
    {isOpen && <span className="font-medium whitespace-nowrap">{label}</span>}
  </button>
);

const StatCard = ({ title, value, subtext, icon, color }) => (
  <motion.div 
    whileHover={{ y: -5 }}
    className="bg-white/5 border border-white/10 rounded-2xl p-6 relative overflow-hidden group"
  >
    <div className={`absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity ${color}`}>
       <SafeIcon icon={icon} className="text-6xl" />
    </div>
    <div className="relative z-10">
      <p className="text-gray-400 text-sm font-medium mb-1">{title}</p>
      <h3 className="text-3xl font-bold mb-2 tracking-tight">{value}</h3>
      <p className="text-xs text-gray-500 uppercase font-semibold tracking-wider">{subtext}</p>
    </div>
  </motion.div>
);

const StatusBadge = ({ status }) => {
  const styles = {
    Completed: 'bg-green-500/10 text-green-400 border-green-500/20',
    Pending: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    Cancelled: 'bg-red-500/10 text-red-400 border-red-500/20',
  };
  
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-bold border ${styles[status] || styles.Pending}`}>
      {status}
    </span>
  );
};

export default AdminDashboard;